'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Icon } from '@benzinga/core-ui';
import { AuthModal } from '@benzinga/auth-ui';
import { EmailCapture } from '@benzinga/forms-ui';
import { Search } from '@benzinga/search-ui';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { SessionContext } from '@benzinga/session-context';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { StockSymbol, Ticker } from '@benzinga/session';
import styled from '@benzinga/themetron';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';
import { WatchlistSelect } from '../WatchlistSelect';
import { TrackingManager } from '@benzinga/tracking-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { QuotesManager } from '@benzinga/quotes-manager';

export interface WatchlistCreateBoxProps {
  actionText?: string;
  buttonLocation: string;
  className?: string;
  createNewWatchlist?: boolean;
  onAddSymbol?: (symbol: StockSymbol) => void;
  onCreateWatchlist?: (watchlist: Watchlist) => void;
  subtitle?: string;
  suggestedTickers?: Ticker[];
  title?: string;
  watchlistName?: string;
}

export const WatchlistCreateBox = ({
  actionText = 'Get Alerts',
  buttonLocation,
  className = '',
  createNewWatchlist = false,
  onAddSymbol,
  onCreateWatchlist,
  subtitle,
  suggestedTickers,
  title = 'Get Alerts to Your Inbox',
  watchlistName = 'My First Watchlist',
}: WatchlistCreateBoxProps) => {
  const session = React.useContext(SessionContext);
  const isLoggedIn = useIsUserLoggedIn();
  const watchlistManager = session.getManager(WatchlistManager);

  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [authModalIsOpen, setAuthModalIsOpen] = useState<boolean>(false);
  const [createdWatchlist, setCreatedWatchlist] = useState<Watchlist | null>(null);
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState<Watchlist | null>(null);
  const [selectedTickers, setSelectedTickers] = useState<Ticker[]>([]);

  const addToWatchlist = useCallback(
    (watchlist: Watchlist) => {
      (async () => {
        const tickers: string[] = selectedTickers.map(selectedTicker => selectedTicker.symbol);
        await watchlistManager.addTickersToWatchlist(watchlist, tickers);
        setSelectedTickers([]);
        // for some reason the createdWatchlist is not stored from the createWatchlist callback
        if (createdWatchlist || createNewWatchlist) {
          onCreateWatchlist?.(watchlist);
        }
      })();
    },
    [selectedTickers, watchlistManager, onCreateWatchlist, createdWatchlist, createNewWatchlist],
  );

  const createWatchlist = useCallback(() => {
    (async () => {
      const res = await watchlistManager.createWatchlist(watchlistName);
      const watchlist = res.ok;
      if (watchlist) {
        setCreatedWatchlist(watchlist);
        await addToWatchlist(watchlist);
        if (!onCreateWatchlist) {
          window.location.href = 'https://www.benzinga.com/profile/portfolio/';
        }
      }
    })();
  }, [addToWatchlist, setCreatedWatchlist, onCreateWatchlist, watchlistName, watchlistManager]);

  const removeTicker = useCallback(
    (ticker: Ticker) => {
      const tickers: Ticker[] = [];
      selectedTickers.forEach(t => {
        if (t.symbol !== ticker.symbol) {
          tickers.push(t);
        }
      });
      setSelectedTickers(tickers);
    },
    [selectedTickers, setSelectedTickers],
  );

  const handleSearchSelect = useCallback(
    (selected: Ticker) => {
      const tickers = [...selectedTickers];
      let contains = false;
      tickers.forEach(t => {
        if (t.symbol === selected.symbol) {
          contains = true;
        }
      });
      if (!contains) {
        if (onAddSymbol) {
          onAddSymbol(selected.symbol);
        }
        tickers.push(selected);
        setSelectedTickers(tickers);
      }
    },
    [onAddSymbol, selectedTickers, setSelectedTickers],
  );

  const handleClosePopup = useCallback(() => setPopupVisible(false), [setPopupVisible]);
  const onEmailCapture = useCallback(
    email => {
      setEmail(email);
      localStorage.setItem('email', email);
      setPopupVisible(false);
      setAuthModalIsOpen(true);
    },
    [setEmail, setPopupVisible],
  );

  const handleGetAlerts = useCallback(() => {
    if (!isLoggedIn) {
      // setPopupVisible(true);
      window.open('https://www.benzinga.com/profile/portfolio/?action=login&utm_source=watchlist-movers', '_blank');
    } else if (selectedWatchlist) {
      addToWatchlist(selectedWatchlist);
    } else {
      createWatchlist();
    }
    if (window !== window.top) {
      window.open('https://www.benzinga.com/profile/portfolio/?action=login&utm_source=watchlist-movers', '_blank');
      session.getManager(TrackingManager).trackLinkEvent('click', {
        button_id: 'get-alert',
        button_label: 'Add Alerts',
        button_location: buttonLocation,
        button_type: 'navigation',
        link_type: 'portfolio-cta',
        link_url: 'https://www.benzinga.com/profile/portfolio/?action=login&utm_source=watchlist-movers',
      });
    }
  }, [addToWatchlist, buttonLocation, createWatchlist, isLoggedIn, selectedWatchlist, session]);

  const handleRegister = useCallback(() => {
    setAuthModalIsOpen(false);
    createWatchlist();
  }, [createWatchlist, setAuthModalIsOpen]);

  const closeModal = useCallback(() => {
    setAuthModalIsOpen(false);
  }, []);

  const handleLogin = useCallback(() => {
    setAuthModalIsOpen(false);
    createWatchlist();
  }, [createWatchlist, setAuthModalIsOpen]);

  const handleSelectWatchlist = useCallback(
    (watchlist: Watchlist) => {
      setSelectedWatchlist(watchlist);
    },
    [setSelectedWatchlist],
  );

  useEffect(() => {
    (async () => {
      if (isLoggedIn) {
        watchlistManager.getWatchlists().then(res => {
          setWatchlists(res.ok ?? []);
          if (res.ok?.length && !createNewWatchlist) setSelectedWatchlist(res.ok[0]);
        });
      }
      if (suggestedTickers) {
        const symbols: string[] = suggestedTickers?.map(ticker => ticker.symbol);

        const quoteManager = session.getManager(QuotesManager);
        const quotesRes = await quoteManager.getDelayedQuotes(symbols);
        const quotes = quotesRes?.ok ? Object.values(quotesRes.ok).filter(quote => quote.symbol) : [];
        setSelectedTickers(quotes);
      }
    })();
  }, [createNewWatchlist, isLoggedIn, session, suggestedTickers, watchlistManager]);

  return (
    <CreateWatchlistWrapper className={`watchlist-create-box ${className}`} subtitle={subtitle}>
      <EmailCapture
        actionText="GET ALERTS"
        description="Enter Your Email to Get Price Alerts on the Stocks You Follow"
        handleClosePopup={handleClosePopup}
        hubspotFormId="17f9a85a-7c14-4917-b107-3a0fd8ace056"
        isOpen={popupVisible}
        onEmailCapture={onEmailCapture}
        title="GET ALERTS"
      />
      <AuthModal
        authMode="register"
        email={email}
        isOpen={authModalIsOpen}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onRequestClose={closeModal}
      />
      <div className="watchlist-create-box-container">
        <div className="watchlist-create-box-wrapper">
          <div className="watchlist-create-box-title">
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(
                  watchlists?.length > 1 && !createNewWatchlist ? 'Add Alerts to Your Watchlist' : title,
                ),
              }}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          {subtitle && <div className="watchlist-create-box-subtitle">{subtitle}</div>}
          {watchlists?.length > 1 && !createNewWatchlist ? (
            <WatchlistSelect onSelect={handleSelectWatchlist} watchlist={selectedWatchlist} watchlists={watchlists} />
          ) : null}
          {selectedTickers.length ? (
            <SelectedTickers removeTicker={removeTicker} selectedTickers={selectedTickers} />
          ) : null}
          <div style={{ marginTop: '0.5rem' }}>
            <Search onSelect={handleSearchSelect} placeholder="Add Tickers..." searchType="ticker" variant="box" />
          </div>
          {selectedTickers?.length ? (
            <div className="watchlist-create-box-action-text" onClick={handleGetAlerts}>
              {watchlists?.length && !createNewWatchlist ? 'Add Alerts' : actionText}
            </div>
          ) : null}
          {watchlists?.length && !selectedTickers.length && !createNewWatchlist ? (
            <div className="customize-wrapper">
              <hr />
              <div className="customize-button-wrapper">
                <a href="https://www.benzinga.com/profile/portfolio/?utm_source=watchlist-create-box">
                  <Button>Customize Watchlists</Button>
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </CreateWatchlistWrapper>
  );
};

const SelectedTickers = ({ removeTicker, selectedTickers }) => {
  return (
    <SelectedTickersContainer className="selected-tickers">
      <div className="selected-tickers-wrapper">
        {selectedTickers.map((ticker: Ticker, i: number) => {
          return (
            <div className="selected-ticker" key={i}>
              <div className="selected-ticker-wrapper">
                <div className="desc">{ticker.name ? ticker.name : ticker.description}</div>
                <div className="symbol">{ticker.symbol}</div>
              </div>
              <div></div>
              <div className="close" onClick={() => removeTicker(ticker)}>
                <Icon icon={faTimes} />
              </div>
            </div>
          );
        })}
      </div>
    </SelectedTickersContainer>
  );
};

const SelectedTickersContainer = styled.div`
  .selected-tickers-wrapper {
    border-top: solid 1px ${({ theme }) => theme.colorPalette.blue200};
    .selected-ticker {
      border-bottom: solid 1px ${({ theme }) => theme.colorPalette.blue200};
      display: flex;
      align-items: center;
      padding: 0.25rem 0;
      .symbol {
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        font-size: ${({ theme }) => theme.fontSize.xs};
        line-height: 1.5;
      }
      .desc {
        font-size: ${({ theme }) => theme.fontSize.sm};
        line-height: 1.5;
      }
      &-wrapper {
        width: 100%;
      }
      .close {
        cursor: pointer;
        align-items: center;
        display: flex;
        .fa {
          font-weight: bold;
          margin-right: 0.5rem;
        }
      }
    }
  }
`;

const CreateWatchlistWrapper = styled.div<Pick<WatchlistCreateBoxProps, 'subtitle'>>`
  .watchlist-create-box {
    &-container {
      height: 100%;
    }
    &-action-text {
      background-color: ${({ theme }) => theme.colorPalette.orange400};
      color: white;
      cursor: pointer;
      margin-top: 0.4rem;
      padding: 0.4rem;
      text-align: center;
    }
    &-wrapper {
      background-color: ${({ theme }) => theme.colorPalette.white};
      border: solid 1px ${({ theme }) => theme.colors.border};
      display: flex;
      flex-direction: column;
      padding: 1rem;
      border-radius: ${({ theme }) => theme.borderRadius.md};
      box-shadow: ${({ theme }) => theme.shadow.md};
      &:hover {
        box-shadow: ${({ theme }) => theme.shadow.lg};
      }
    }
    &-title {
      font-size: ${({ theme }) => theme.fontSize.lg};
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      border-bottom: solid ${({ subtitle }) => (subtitle ? '8px' : 0)} ${({ theme }) => theme.colorPalette.blue500};
      padding-bottom: 0.5rem;
      margin-bottom: 0.5rem;
    }
    &-subtitle {
      padding-bottom: 0.5rem;
    }
  }
  .watchlist-create-box-container {
    max-width: 400px;
    margin: auto;
  }
  .customize-wrapper {
    padding-top: 0.5rem;
  }
  .customize-button-wrapper {
    padding-top: 0.5rem;
    text-align: center;
    button {
      display: inline-block;
      width: 100%;
    }
  }
  .ticker-search-box {
    height: 300px;
    max-height: 300px;
  }
  .ticker-search-menulist {
    height: 100%;
    overflow-x: hidden;
    max-height: 250px;
  }
  .searchbar-wrapper {
    margin-bottom: 8px;
    max-width: 100% !important;
  }
`;
